import { fromBinary, toBinary } from '@bufbuild/protobuf';

import { PairingOfferV1Schema } from './generated/mlo/foundation/v1/foundation_pb.js';
import type { PairingOfferV1 } from './generated/mlo/foundation/v1/foundation_pb.js';

const QR_PREFIX = 'mlo1:';
const BASE64URL = /^[A-Za-z0-9_-]+$/;

type BinaryRuntime = typeof globalThis & {
  atob?: (value: string) => string;
  btoa?: (value: string) => string;
  Buffer?: {
    from(value: Uint8Array | string, encoding?: string): { toString(encoding: string): string };
  };
};

const runtime = globalThis as BinaryRuntime;

function encodeBase64Url(bytes: Uint8Array): string {
  const binary = String.fromCharCode(...bytes);
  const base64 = runtime.btoa
    ? runtime.btoa(binary)
    : runtime.Buffer?.from(bytes).toString('base64');
  if (!base64) throw new Error('Base64 encoding is unavailable');
  return base64.replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');
}

function decodeBase64Url(value: string): Uint8Array {
  if (!value || !BASE64URL.test(value) || value.length % 4 === 1) {
    throw new Error('Invalid mlo1 QR payload');
  }
  const base64 = value
    .replaceAll('-', '+')
    .replaceAll('_', '/')
    .padEnd(Math.ceil(value.length / 4) * 4, '=');
  if (runtime.atob) {
    const binary = runtime.atob(base64);
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
  }
  if (runtime.Buffer)
    return Uint8Array.from(runtime.Buffer.from(base64, 'base64').toString('binary'), (character) =>
      character.charCodeAt(0),
    );
  throw new Error('Base64 decoding is unavailable');
}

/** Encode the canonical QR/manual pairing payload. */
export function encodePairingOffer(offer: PairingOfferV1): string {
  return `${QR_PREFIX}${encodeBase64Url(toBinary(PairingOfferV1Schema, offer))}`;
}

/** Decode only canonical mlo1 payloads; JSON/CBOR or non-canonical bytes are rejected. */
export function decodePairingOffer(payload: string): PairingOfferV1 {
  if (!payload.startsWith(QR_PREFIX)) throw new Error('Unsupported pairing payload');
  const encoded = payload.slice(QR_PREFIX.length);
  const offer = fromBinary(PairingOfferV1Schema, decodeBase64Url(encoded));
  if (encodePairingOffer(offer) !== payload) throw new Error('Non-canonical pairing payload');
  return offer;
}
